import React, { useMemo, useState } from 'react';
import { useData } from '../../store/DataContext';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { PackageCheckIcon, PlusIcon, RotateCcwIcon, Trash2Icon } from 'lucide-react';
type DrugForm = {
  medicineId: string;
  batchNumber: string;
  quantity: string;
  buyingPrice: string;
  sellingPrice: string;
  manufactureDate: string;
  expiryDate: string;
};
type StagedDrug = DrugForm & {
  id: string;
  medicineName: string;
  unit: string;
};
const emptyDrug: DrugForm = {
  medicineId: '',
  batchNumber: '',
  quantity: '',
  buyingPrice: '',
  sellingPrice: '',
  manufactureDate: '',
  expiryDate: ''
};
export function ReceiveStock() {
  const {
    medicines,
    suppliers,
    purchaseOrders,
    receiveStock,
    currentUser
  } = useData();
  const manager = currentUser?.role === 'Pharmacy Manager';
  const [supplierId, setSupplierId] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [purchaseOrderId, setPurchaseOrderId] = useState('');
  const [notes, setNotes] = useState('');
  const [drug, setDrug] = useState<DrugForm>(emptyDrug);
  const [stagedDrugs, setStagedDrugs] = useState<StagedDrug[]>([]);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const activeOrders = useMemo(() => purchaseOrders.filter(order => order.kind !== 'Receipt' && ['Pending', 'Partially Received'].includes(order.status)), [purchaseOrders]);
  const selectedOrder = activeOrders.find(order => order.id === purchaseOrderId);
  const availableMedicines = selectedOrder ? medicines.filter(medicine => selectedOrder.lines.some(line => line.medicineId === medicine.id && line.quantityReceived < line.quantityOrdered)) : medicines.filter(medicine => medicine.status === 'Active');
  const addDrug = () => {
    setError('');
    setMessage('');
    const medicine = medicines.find(item => item.id === drug.medicineId);
    if (!medicine) return setError('Select a medicine before adding it to the receipt.');
    if (!Number.isFinite(Number(drug.quantity)) || Number(drug.quantity) <= 0) return setError('Enter a quantity greater than zero.');
    if (!Number.isFinite(Number(drug.buyingPrice)) || Number(drug.buyingPrice) < 0 || !Number.isFinite(Number(drug.sellingPrice)) || Number(drug.sellingPrice) < 0) return setError('Enter valid buying and selling prices.');
    if (!drug.manufactureDate || !drug.expiryDate || new Date(drug.expiryDate) <= new Date(drug.manufactureDate)) return setError('Enter manufacture and expiry dates with expiry after manufacture.');
    setStagedDrugs(current => [...current, {
      ...drug,
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      medicineName: medicine.name,
      unit: medicine.unit
    }]);
    setDrug(emptyDrug);
  };
  const clearForm = () => {
    setSupplierId('');
    setInvoiceNumber('');
    setPurchaseOrderId('');
    setNotes('');
    setDrug(emptyDrug);
    setStagedDrugs([]);
    setError('');
    setMessage('');
  };
  const updateInventory = async () => {
    setError('');
    setMessage('');
    if (!supplierId || !invoiceNumber.trim() || !stagedDrugs.length) {
      setError('Supplier, invoice number, and at least one staged drug are required.');
      return;
    }
    setSaving(true);
    const result = await receiveStock({
      supplierId,
      invoiceNumber: invoiceNumber.trim(),
      purchaseOrderId: purchaseOrderId || undefined,
      notes,
      items: stagedDrugs.map(({
        medicineId,
        batchNumber,
        quantity,
        buyingPrice,
        sellingPrice,
        manufactureDate,
        expiryDate
      }) => ({
        medicineId,
        batchNumber: batchNumber.trim() || undefined,
        quantity: Number(quantity),
        buyingPrice: Number(buyingPrice),
        sellingPrice: Number(sellingPrice),
        manufactureDate,
        expiryDate
      }))
    });
    setSaving(false);
    if (!result.ok) {
      setError(result.error || 'Inventory could not be updated.');
      return;
    }
    clearForm();
    setMessage('Inventory Updated Successfully.');
  };
  if (!manager) return <div className="max-w-3xl rounded-xl border border-amber-200 bg-amber-50 p-8" data-id="element-5582" data-mp="31sn3zs6vrtdr">
        <h2 className="text-lg font-bold text-amber-900" data-id="element-5583" data-mp="36nsxpd6iycce">
          Goods receiving is handled by the Pharmacist
        </h2>
        <p className="mt-2 text-sm leading-6 text-amber-800" data-id="element-5584" data-mp="3gk5r09y5qo6m">
          Only the Pharmacist (Pharmacy Manager) records incoming deliveries and
          enters supplier, invoice, batch, pricing, and expiry details. Please
          hand the delivery note to the Pharmacist to be entered.
        </p>
      </div>;
  return <div className="max-w-6xl space-y-5" data-id="element-5585" data-mp="14walcbq5z405">
      <div data-id="element-5586" data-mp="14yn2wns0fcco">
        <h2 className="text-xl font-bold text-slate-950" data-id="element-5587" data-mp="14ywugfspdrkh">Receive New Stock</h2>
        <p className="mt-1 text-sm text-slate-500" data-id="element-5588" data-mp="zxpmafmneqda">
          Stage a complete delivery first. Inventory is changed only once, after
          the entire receipt passes validation.
        </p>
      </div>
      {message && <p role="status" className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-800" data-id="element-5589" data-mp="1nn1t58ui6mcf">
          {message}
        </p>}
      {error && <p role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-800" data-id="element-5590" data-mp="1dxrybl89ypkm">
          {error}
        </p>}

      <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-id="element-5591" data-mp="26m5x4376tpx8">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3" data-id="element-5592" data-mp="4odm09lyrrr7">
          <Select label="Supplier" required value={supplierId} onChange={event => setSupplierId(event.target.value)} options={[{
          value: '',
          label: 'Select supplier'
        }, ...suppliers.filter(supplier => supplier.status === 'Active').map(supplier => ({
          value: supplier.id,
          label: supplier.name
        }))]} data-id="element-5593" data-mp="4p5jjpz4fzk4" />
          <Input label="Invoice number" required value={invoiceNumber} onChange={event => setInvoiceNumber(event.target.value)} data-id="element-5594" data-mp="385m2ap11mjjt" />
          <Select label="Purchase order (optional)" value={purchaseOrderId} onChange={event => {
          const order = activeOrders.find(item => item.id === event.target.value);
          setPurchaseOrderId(event.target.value);
          setSupplierId(order?.supplierId || supplierId);
          setDrug(emptyDrug);
        }} options={[{
          value: '',
          label: 'Independent receipt'
        }, ...activeOrders.map(order => ({
          value: order.id,
          label: `${order.orderNumber} · ${order.supplierName}`
        }))]} data-id="element-5595" data-mp="16dzawmd8ommt" />
        </div>
        <label className="mt-4 block text-sm font-medium text-slate-700" data-id="element-5596" data-mp="39lzxtrkh68sk">
          Receiving remarks{' '}
          <textarea value={notes} onChange={event => setNotes(event.target.value)} rows={2} className="mt-1 w-full rounded-md border border-slate-300 p-2 text-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600" placeholder="Delivery inspection or receipt remarks" data-id="element-5597" data-mp="3ohftu24m8erl" />
        </label>
      </section>

      <section className="rounded-xl border border-teal-200 bg-teal-50/40 p-5" data-id="element-5598" data-mp="13b4ppiu8cdlw">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2" data-id="element-5599" data-mp="18ar25q28hptn">
          <div data-id="element-5600" data-mp="1d43g969aee85">
            <h3 className="font-bold text-slate-900" data-id="element-5601" data-mp="1d3k8ujh9j88d">Add drug to receipt</h3>
            <p className="mt-1 text-xs text-slate-600" data-id="element-5602" data-mp="1i13kdbmkt6wm">
              Leave batch number blank to generate it centrally when inventory
              is updated.
            </p>
          </div>
          <span className="text-xs font-semibold text-teal-800" data-id="element-5603" data-mp="pco9906kf8qk">
            Received by: {currentUser?.fullName || 'Signed-in user'}
          </span>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4" data-id="element-5604" data-mp="uex0ns3si2qk">
          <Select label="Medicine" required value={drug.medicineId} onChange={event => {
          const selectedMedicine = medicines.find(medicine => medicine.id === event.target.value);
          setDrug({
            ...drug,
            medicineId: event.target.value,
            buyingPrice: selectedMedicine ? String(selectedMedicine.buyingPrice) : '',
            sellingPrice: selectedMedicine ? String(selectedMedicine.sellingPrice) : ''
          });
        }} options={[{
          value: '',
          label: 'Select medicine'
        }, ...availableMedicines.map(medicine => ({
          value: medicine.id,
          label: `${medicine.name} · ${medicine.drugCode}`
        }))]} data-id="element-5605" data-mp="yc1j3tylpzbt" />
          <Input label="Batch number (optional)" value={drug.batchNumber} onChange={event => setDrug({
          ...drug,
          batchNumber: event.target.value
        })} data-id="element-5606" data-mp="2xmxgkdclyomt" />
          <Input label="Quantity received" required type="number" min="1" value={drug.quantity} onChange={event => setDrug({
          ...drug,
          quantity: event.target.value
        })} data-id="element-5607" data-mp="1hp98y54drjbo" />
          <Input label="Buying price (ZMW)" required type="number" min="0" step="0.01" value={drug.buyingPrice} disabled={!manager} onChange={event => setDrug({
          ...drug,
          buyingPrice: event.target.value
        })} data-id="element-5608" data-mp="1lsp1ap9f2xik" />
          <Input label="Selling price (ZMW)" required type="number" min="0" step="0.01" value={drug.sellingPrice} onChange={event => setDrug({
          ...drug,
          sellingPrice: event.target.value
        })} data-id="element-5609" data-mp="12zuysuszcxv8" />
          <Input label="Manufacture date" required type="date" max={new Date().toISOString().slice(0, 10)} value={drug.manufactureDate} onChange={event => setDrug({
          ...drug,
          manufactureDate: event.target.value
        })} data-id="element-5610" data-mp="2qrc9wfp5ywrh" />
          <Input label="Expiry date" required type="date" min={drug.manufactureDate || undefined} value={drug.expiryDate} onChange={event => setDrug({
          ...drug,
          expiryDate: event.target.value
        })} data-id="element-5611" data-mp="39pohhx0ffyky" />
          <div className="flex items-end" data-id="element-5612" data-mp="1k9yvzwuds47u">
            <Button type="button" onClick={addDrug} className="w-full bg-teal-600 hover:bg-teal-700" data-id="element-5613" data-mp="1p9tk5smrufp7">
              <PlusIcon className="mr-2 h-4 w-4" data-id="element-5614" data-mp="157754peve6k8" /> Add Drug
            </Button>
          </div>
        </div>
      </section>

      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-5615" data-mp="11881r98sehuw">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-4" data-id="element-5616" data-mp="wdakjf1ct26x">
          <div data-id="element-5617" data-mp="mh7kqftz5fn5">
            <h3 className="font-bold text-slate-900" data-id="element-5618" data-mp="mgz3ycuqdq6o">Temporary order list</h3>
            <p className="mt-1 text-sm text-slate-500" data-id="element-5619" data-mp="meuvripsu50l">
              {stagedDrugs.length} line{stagedDrugs.length === 1 ? '' : 's'}{' '}
              staged · total{' '}
              {new Intl.NumberFormat('en-ZM', {
              style: 'currency',
              currency: 'ZMW'
            }).format(stagedDrugs.reduce((sum, item) => sum + Number(item.quantity) * Number(item.buyingPrice), 0))}
            </p>
          </div>
          <Button variant="outline" onClick={clearForm} data-id="element-5620" data-mp="2adyo1vov3bec">
            <RotateCcwIcon className="mr-2 h-4 w-4" data-id="element-5621" data-mp="2fe35bnxlga5m" /> Clear Form
          </Button>
        </div>
        <div className="overflow-x-auto" data-id="element-5622" data-mp="2k4t9ixuaa6yq">
          <table className="min-w-[900px] w-full divide-y divide-slate-100" data-id="element-5623" data-mp="2k5umx8yrv5jm">
            <thead className="bg-slate-50" data-id="element-5624" data-mp="2p4e99aha2jci">
              <tr data-id="element-5625" data-mp="2p3ctispbh8og">
                {['Medicine', 'Batch', 'Quantity', 'Buying price', 'Selling price', 'Dates', ''].map(heading => <th key={heading} className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500" data-id="element-5626" data-mp="21dilqpjuuab2">
                    {heading}
                  </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-5627" data-mp="3gtr5gjdrsaxh">
              {stagedDrugs.map(item => <tr key={item.id} data-id="element-5628" data-mp="3feog1nb9a5j0">
                  <td className="px-5 py-3 text-sm font-semibold text-slate-900" data-id="element-5629" data-mp="3ffq36q5jqih3">
                    {item.medicineName}
                    <span className="ml-2 text-xs font-normal text-slate-500" data-id="element-5630" data-mp="3pbt6w0g1qp65">
                      {item.unit}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-sm text-slate-600" data-id="element-5631" data-mp="3kf2p98olflim">
                    {item.batchNumber || 'Generated on update'}
                  </td>
                  <td className="px-5 py-3 text-sm text-slate-700" data-id="element-5632" data-mp="3tefzw4ogeg4x">
                    {item.quantity}
                  </td>
                  <td className="px-5 py-3 text-sm text-slate-700" data-id="element-5633" data-mp="bwn34zqxm4lz">
                    ZMW {item.buyingPrice}
                  </td>
                  <td className="px-5 py-3 text-sm text-slate-700" data-id="element-5634" data-mp="70fw13msun2g">
                    ZMW {item.sellingPrice}
                  </td>
                  <td className="px-5 py-3 text-xs text-slate-500" data-id="element-5635" data-mp="gx2e5knqr00s">
                    Mfg {item.manufactureDate}
                    <br data-id="element-5636" data-mp="zlv179hxs88t" />
                    Exp {item.expiryDate}
                  </td>
                  <td className="px-5 py-3 text-right" data-id="element-5637" data-mp="14ile5nyg2nq0">
                    <button type="button" onClick={() => setStagedDrugs(current => current.filter(staged => staged.id !== item.id))} className="rounded p-2 text-red-600 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500" aria-label={`Remove ${item.medicineName}`} data-id="element-5638" data-mp="qjvimcr9ww3r">
                      <Trash2Icon className="h-4 w-4" data-id="element-5639" data-mp="7rkkew7kdmbz" />
                    </button>
                  </td>
                </tr>)}
              {!stagedDrugs.length && <tr data-id="element-5640" data-mp="3u2ot2gor94tp">
                  <td colSpan={7} className="px-5 py-12 text-center text-sm text-slate-500" data-id="element-5641" data-mp="2uawjkztpiq9">
                    No medicines have been staged. Add a drug to begin this
                    receipt.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
        <div className="flex justify-end border-t border-slate-100 px-5 py-4" data-id="element-5642" data-mp="1vrodrqu0oth6">
          <Button onClick={updateInventory} disabled={saving || !stagedDrugs.length} className="bg-teal-600 hover:bg-teal-700 disabled:cursor-not-allowed disabled:bg-slate-300" data-id="element-5643" data-mp="1qtd178cc84v9">
            <PackageCheckIcon className="mr-2 h-4 w-4" data-id="element-5644" data-mp="2jhijzpjtzjsg" />
            {saving ? 'Updating inventory…' : 'Update Inventory'}
          </Button>
        </div>
      </section>
    </div>;
}